import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState
} from "react";

export const columnXSpacing = 4;
export const nameColumnWidth = 220 - columnXSpacing;
export const dayColumnWidth = 92.8;

export const MIN_NAME_COLUMN_WIDTH = 100;
export const MIN_DAY_COLUMN_WIDTH = 50;

// Default width to use if container measurement fails
export const DEFAULT_CONTAINER_WIDTH = 1024;

export const ColumnWidthContext = createContext<{
  nameColumnWidth: number;
  dayColumnWidth: number;
  daysCount: number;
  totalWidth: number;
  isOverflowing: boolean;
}>({
  nameColumnWidth,
  dayColumnWidth,
  daysCount: 0,
  totalWidth: 0,
  isOverflowing: false
});

export const useColumnWidth = () => useContext(ColumnWidthContext);

// Calculate total width based on days count
export const calculateTotalWidth = (daysCount: number) => {
  // For 5 or fewer days, use the default width
  if (daysCount <= 5) {
    return 1024;
  }
  // For more than 5 days, add (dayColumnWidth+columnXSpacing)px for each additional day
  const extraDays = daysCount - 5;
  return 1024 + extraDays * (dayColumnWidth + columnXSpacing);
};

// Proportionally resize columns when table exceeds container width
export const calculateAdjustedWidths = (
  containerWidth: number,
  daysCount: number,
  originalNameWidth: number = nameColumnWidth,
  originalDayWidth: number = dayColumnWidth
) => {
  const totalWidth = calculateTotalWidth(daysCount);

  // If no valid container width or table fits within container, return original sizes
  if (!containerWidth || containerWidth <= 0 || totalWidth <= containerWidth) {
    return {
      nameColumnWidth: originalNameWidth,
      dayColumnWidth: originalDayWidth,
      totalWidth,
      isOverflowing: false
    };
  }

  // Calculate how much we need to shrink
  const shrinkRatio = containerWidth / totalWidth;

  // Calculate new widths based on shrink ratio
  let adjustedNameWidth = Math.max(
    originalNameWidth * shrinkRatio,
    MIN_NAME_COLUMN_WIDTH
  );
  let adjustedDayWidth = Math.max(
    originalDayWidth * shrinkRatio,
    MIN_DAY_COLUMN_WIDTH
  );

  // If using minimum widths still exceeds container, prioritize showing minimum day columns
  const minTotalWidth =
    MIN_NAME_COLUMN_WIDTH + (MIN_DAY_COLUMN_WIDTH + columnXSpacing) * daysCount;
  if (minTotalWidth > containerWidth) {
    adjustedNameWidth = MIN_NAME_COLUMN_WIDTH;
    adjustedDayWidth = MIN_DAY_COLUMN_WIDTH;
  }

  // Recalculate total width with adjusted column widths
  const adjustedTotalWidth =
    adjustedNameWidth + (adjustedDayWidth + columnXSpacing) * daysCount;

  return {
    nameColumnWidth: adjustedNameWidth,
    dayColumnWidth: adjustedDayWidth,
    totalWidth: adjustedTotalWidth,
    isOverflowing: true
  };
};

export const calculateAllSizes = (
  daysCount: number,
  containerWidth?: number
) => {
  const totalWidth = calculateTotalWidth(daysCount);

  if (containerWidth && containerWidth > 0) {
    return calculateAdjustedWidths(containerWidth, daysCount);
  }

  return {
    nameColumnWidth,
    dayColumnWidth,
    totalWidth,
    isOverflowing: false
  };
};

// Helper function to measure an element
const measureElement = (element: HTMLElement | null): number => {
  if (!element) return 0;

  // Try to get the width using various methods
  const rect = element.getBoundingClientRect();
  if (rect.width) return rect.width;

  // If getBoundingClientRect fails, try offsetWidth
  if (element.offsetWidth) return element.offsetWidth;

  // If all else fails, try to get computed style
  const computedStyle = window.getComputedStyle(element);
  const widthStr = computedStyle.width;
  if (widthStr && widthStr !== "auto") {
    const width = Number.parseFloat(widthStr);
    if (!Number.isNaN(width)) return width;
  }

  // If we can't get width, check for max-width
  const maxWidthStr = computedStyle.maxWidth;
  if (maxWidthStr && maxWidthStr !== "none") {
    const maxWidth = Number.parseFloat(maxWidthStr);
    if (!Number.isNaN(maxWidth)) return maxWidth;
  }

  return DEFAULT_CONTAINER_WIDTH; // Default fallback
};

export const ColumnWidthProvider = ({
  children,
  daysCount = 5,
  containerRef
}: {
  children: React.ReactNode;
  daysCount: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const [containerWidth, setContainerWidth] = useState<number>(
    DEFAULT_CONTAINER_WIDTH
  );

  // Adjust daysCount for rendering (always show at least 5 columns)
  const displayDaysCount = daysCount < 5 ? 5 : daysCount;

  // Effect to measure container width - using useLayoutEffect for more immediate measurement
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const measuredWidth = measureElement(containerRef.current);
        setContainerWidth(
          measuredWidth > 0 ? measuredWidth : DEFAULT_CONTAINER_WIDTH
        );
      }
    };

    // Initial measurement
    updateWidth();

    // Set up resize observer to track container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure DOM is updated
      setTimeout(updateWidth, 0);
    });
    resizeObserver.observe(containerRef.current);

    // Also measure on window resize as a fallback
    window.addEventListener("resize", updateWidth);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [containerRef]);

  // Calculate sizes based on container width
  const {
    nameColumnWidth: adjustedNameWidth,
    dayColumnWidth: adjustedDayWidth,
    totalWidth,
    isOverflowing
  } = calculateAllSizes(displayDaysCount, containerWidth);

  // Create the context value
  const value = React.useMemo(
    () => ({
      nameColumnWidth: adjustedNameWidth,
      dayColumnWidth: adjustedDayWidth,
      daysCount: displayDaysCount,
      totalWidth,
      isOverflowing
    }),
    [
      adjustedNameWidth,
      adjustedDayWidth,
      displayDaysCount,
      totalWidth,
      isOverflowing
    ]
  );

  return (
    <ColumnWidthContext.Provider value={value}>
      {children}
    </ColumnWidthContext.Provider>
  );
};
